import React, { useState } from 'react';
import { User, Settings, LogOut, ChevronDown, Building2, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/hooks/useAuthStore';
import { ROLE_LABELS } from '@/types/user';
import type { UserRole } from '@/types/user';

export interface UserMenuProps {
  onProfile?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  className?: string;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  onProfile,
  onSettings,
  onLogout,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email ?? 'User');

  const initials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : (user?.email?.[0] ?? 'U').toUpperCase();

  const roleLabel = user?.role ? (ROLE_LABELS[user.role as UserRole] ?? user.role) : '';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 rounded-xl',
          'text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100',
          'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500',
          className
        )}
      >
        {/* Avatar */}
        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs font-semibold">{initials}</span>
        </div>

        {/* Name + org (hidden on small screens) */}
        <div className="hidden md:flex flex-col items-start min-w-0">
          <span className="text-sm font-medium text-secondary-800 leading-tight truncate max-w-[140px]">
            {displayName}
          </span>
          {user?.organisationName ? (
            <span className="text-xs text-secondary-500 leading-tight truncate max-w-[140px] flex items-center gap-1">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              {user.organisationName}
            </span>
          ) : (
            <span className="text-xs text-secondary-400 leading-tight">{roleLabel}</span>
          )}
        </div>

        <ChevronDown className="w-4 h-4 text-secondary-400 flex-shrink-0" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-secondary-200 z-20 overflow-hidden">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-secondary-100 bg-secondary-50">
              <p className="text-sm font-semibold text-secondary-900 truncate">{displayName}</p>
              <p className="text-xs text-secondary-500 truncate">{user?.email}</p>
              {roleLabel && (
                <p className="text-xs text-secondary-400 mt-0.5 truncate">{roleLabel}</p>
              )}
              {user?.organisationName && (
                <p className="text-xs text-primary-600 mt-0.5 flex items-center gap-1 truncate">
                  <Building2 className="w-3 h-3 flex-shrink-0" />
                  {user.organisationName}
                </p>
              )}
            </div>

            <div className="py-1">
              {onProfile && (
                <button
                  onClick={() => {
                    onProfile();
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                >
                  <User className="w-4 h-4 mr-3 text-secondary-400" />
                  Profile
                </button>
              )}
              {onSettings && (
                <button
                  onClick={() => {
                    onSettings();
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                >
                  <Settings className="w-4 h-4 mr-3 text-secondary-400" />
                  Settings
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/help');
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
              >
                <HelpCircle className="w-4 h-4 mr-3 text-secondary-400" />
                Help & Documentation
              </button>
              <hr className="my-1 border-secondary-100" />
              {onLogout && (
                <button
                  onClick={() => {
                    onLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign out
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
